// Desktop variant of StyleOutfit — two-column editorial layout.
// Left: sticky photo + composer. Right: tabs, filters, and gallery grid.
import {
  Sparkles, Wand2, Camera, Upload, Coins, AlertTriangle, ImageIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { CustomizeOutfitDialog } from "@/components/CustomizeOutfitDialog";
import { CustomizeOutfitInline } from "./CustomizeOutfitInline";
import { CreditsChip } from "@/components/CreditsChip";
import { BuyCreditsDialog } from "@/components/BuyCreditsDialog";
import { HighDemandDialog } from "@/components/HighDemandDialog";
import { GenerationOverlay } from "@/components/GenerationOverlay";
import { PhotoTips } from "@/components/PhotoTips";
import { BottomSheet } from "@/components/BottomSheet";
import { PaywallModal } from "@/components/PaywallModal";
import { GENDER_OPTIONS } from "@/lib/style-types";
import { cn } from "@/lib/utils";
import { SEO } from "@/components/SEO";
import { STAGES, type StyleOutfitCtx, type Tab } from "./useStyleOutfit";
import { ImageWithSkeleton } from "@/components/ui/image-with-skeleton";
import { StitchDesktopHeader } from "@/components/stitch/StitchDesktopHeader";

export const StyleOutfitDesktop = (ctx: StyleOutfitCtx) => {
  const {
    tab, setTab, gender, setGender,
    outfitCategory, setOutfitCategory, itemType, setItemType,
    userPhotoPreview, busy, stageIdx, genError,
    customOpen, setCustomOpen,
    buyOpen, setBuyOpen, buyDefaultPack, setBuyDefaultPack,
    outOfCreditsOpen, setOutOfCreditsOpen,
    highDemandOpen, setHighDemandOpen,
    paywall, setPaywall, handlePaywallDismiss, handlePaywallBuy,
    previewVibe, setPreviewVibe, previewLook, setPreviewLook, previewItem, setPreviewItem,
    customItemPreview, customItemSheetOpen, setCustomItemSheetOpen,
    setCustomItemFile, setCustomItemPreview,
    customItemLabel, setCustomItemLabel,
    vibeMode, setVibeMode,
    visibleVibes, visibleOutfits, visibleItems,
    outfitCategoryChips, itemTypeChips,
    onPickPhoto,
    openVibe, confirmVibe, confirmLook, confirmItem, confirmCustomItem,
    generateOutfit, setGenError, lastAttemptRef,
  } = ctx;

  // Desktop has an extra "Customize" tab not present in the shared `Tab` type.
  type DesktopTab = Tab | "customize";
  const tabs: Array<{ id: DesktopTab; label: string; sub: string }> = [
    { id: "vibes", label: "Vibes", sub: "Scene & mood" },
    { id: "outfits", label: "Outfits", sub: "Specific looks" },
    { id: "items", label: "Items", sub: "Swap one piece" },
    { id: "customize", label: "Customize", sub: "Prompt, links & photos" },
  ];

  const isCustomize = tab === ("customize" as DesktopTab);
  const currentTab = tabs.find((t) => t.id === (isCustomize ? "customize" : tab));
  const setTabAny = (id: DesktopTab) => setTab(id as Tab);

  return (
    <>
      <SEO />
      <StitchDesktopHeader
        eyebrow="Style Lab"
        title="Style Studio"
        onBuyCredits={() => setBuyOpen(true)}
      />
      <div className="mx-auto w-full max-w-[1400px] px-8 py-10 animate-fade-in xl:px-12">
        {/* Page intro */}
        <header className="mb-8">
          <h1 className="font-display flex items-center gap-3 text-4xl font-bold uppercase tracking-tight">
            <Sparkles className="h-7 w-7 text-primary" /> Style
          </h1>
          <p className="mt-2 max-w-xl text-sm text-muted-foreground">
            Pick a vibe, dress in a complete outfit, or swap a single piece — all on your photo, in seconds.
          </p>
          <span className="mt-3 hidden items-center gap-1.5 rounded-full border border-border/60 bg-card/40 px-3 py-1.5 text-xs font-medium text-muted-foreground backdrop-blur md:inline-flex">
            <Coins className="h-3.5 w-3.5 text-primary" /> 1 credit per generation
          </span>
        </header>

        {/* Two column layout */}
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[360px_1fr] xl:grid-cols-[400px_1fr] xl:gap-10">
          {/* LEFT — sticky composer */}
          <aside className="lg:sticky lg:top-8 lg:self-start">
            <div className="rounded-3xl border border-white/10 bg-card/60 p-6 shadow-elevated backdrop-blur-xl">
              <p className="font-display text-[10px] font-semibold uppercase tracking-[0.3em] text-muted-foreground">
                Step 1 · Your photo
              </p>

              {/* Photo frame */}
              <div className="relative mt-3 overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-primary/10 via-background to-accent/10 aspect-[3/4]">
                {userPhotoPreview ? (
                  <>
                    <img src={userPhotoPreview} alt="You" className="absolute inset-0 h-full w-full object-cover" />
                    <div aria-hidden className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                  </>
                ) : (
                  <div className="flex h-full w-full flex-col items-center justify-center gap-3 text-muted-foreground">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/15 ring-1 ring-primary/30">
                      <Camera className="h-7 w-7 text-primary" />
                    </div>
                    <p className="text-sm font-medium">Upload a clear full-body photo</p>
                    <p className="px-6 text-center text-xs">Face stays exactly the same — only the outfit changes.</p>
                  </div>
                )}

                <label className="absolute bottom-3 right-3 cursor-pointer">
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => onPickPhoto(e.target.files?.[0])} />
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-background/90 px-3.5 py-2 text-xs font-semibold text-foreground shadow-elevated backdrop-blur transition hover:bg-background">
                    <Upload className="h-3.5 w-3.5" /> {userPhotoPreview ? "Change" : "Upload"}
                  </span>
                </label>
              </div>

              <PhotoTips className="mt-4" />

              {/* Composer */}
              <div className="mt-5 space-y-3">
                <p className="font-display text-[10px] font-semibold uppercase tracking-[0.3em] text-muted-foreground">
                  Step 2 · Generate
                </p>
                <Button
                  variant="outline"
                  className="h-12 w-full justify-start rounded-xl border-white/10 bg-background/50 text-sm font-medium hover:border-primary/40"
                  disabled={busy}
                  onClick={() => setTabAny("customize")}
                >
                  <Wand2 className="mr-2 h-4 w-4 text-primary" />
                  Customize with prompt, links & photos
                </Button>
                <p className="text-[11px] leading-relaxed text-muted-foreground">
                  Or pick a curated look from the gallery →
                </p>
              </div>
            </div>
          </aside>

          {/* RIGHT — tabs + gallery */}
          <section className="min-w-0">
            {/* Segmented tabs */}
            <div className="mb-6 inline-flex rounded-2xl border border-white/10 bg-card/60 p-1.5 shadow-soft backdrop-blur-xl">
              {tabs.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTabAny(t.id)}
                  className={cn(
                    "rounded-xl px-5 py-2.5 text-sm font-semibold transition-all",
                    tab === t.id
                      ? "bg-primary text-primary-foreground shadow-elevated"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* Section heading + filters row */}
            <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
              <div>
                <h2 className="font-display text-2xl font-bold tracking-tight">
                  {currentTab?.label}
                </h2>
                <p className="text-sm text-muted-foreground">{currentTab?.sub}</p>
              </div>

              {!isCustomize && tab !== "vibes" && (
                <div className="flex gap-2">
                  {GENDER_OPTIONS.map((g) => (
                    <button
                      key={g.id}
                      type="button"
                      onClick={() => setGender(g.id)}
                      className={cn(
                        "rounded-full border px-4 py-1.5 text-xs font-semibold transition-all",
                        gender === g.id
                          ? "border-primary bg-primary text-primary-foreground shadow-soft"
                          : "border-white/10 bg-card/40 text-muted-foreground hover:border-primary/40 hover:text-foreground",
                      )}
                    >
                      <span className="mr-1">{g.emoji}</span>
                      {g.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Inline customize panel — desktop replaces the modal dialog. */}
            {isCustomize && (
              <CustomizeOutfitInline
                busy={busy}
                onGenerate={(d) => generateOutfit(d, "Custom", "custom")}
              />
            )}

            {/* Category chips */}
            {!isCustomize && tab === "outfits" && outfitCategoryChips.length > 0 && (
              <div className="mb-6 flex flex-wrap gap-2">
                <FilterChip active={outfitCategory === "all"} onClick={() => setOutfitCategory("all")}>All</FilterChip>
                {outfitCategoryChips.map((c) => (
                  <FilterChip key={c.id} active={outfitCategory === c.id} onClick={() => setOutfitCategory(c.id)}>
                    <span className="mr-1">{c.emoji}</span>{c.label}
                  </FilterChip>
                ))}
              </div>
            )}
            {tab === "items" && itemTypeChips.length > 0 && (
              <div className="mb-6 flex flex-wrap gap-2">
                <FilterChip active={itemType === "all"} onClick={() => setItemType("all")}>All</FilterChip>
                {itemTypeChips.map((c) => (
                  <FilterChip key={c.id} active={itemType === c.id} onClick={() => setItemType(c.id)}>
                    <span className="mr-1">{c.emoji}</span>{c.label}
                  </FilterChip>
                ))}
              </div>
            )}

            {/* Galleries */}
            {tab === "vibes" && (
              visibleVibes.length === 0 ? <EmptyState text="No vibes for this audience yet." /> : (
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-4">
                  {visibleVibes.map((v) => (
                    <ThumbCard
                      key={v.id}
                      label={v.label}
                      image={v.image}
                      disabled={busy}
                      onClick={() => openVibe(v)}
                      badge={!v.genders.includes("men") ? "Women only" : undefined}
                    />
                  ))}
                </div>
              )
            )}

            {tab === "outfits" && (
              visibleOutfits.length === 0 ? <EmptyState text="No outfits in this category yet." /> : (
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-4">
                  {visibleOutfits.map((l) => (
                    <ThumbCard key={l.id} label={l.label} image={l.image} disabled={busy} onClick={() => setPreviewLook(l)} />
                  ))}
                </div>
              )
            )}

            {tab === "items" && (
              <>
                <div className="mb-5 flex items-start gap-2.5 rounded-xl border border-white/10 bg-card/40 px-4 py-3 text-xs text-muted-foreground backdrop-blur">
                  <ImageIcon className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  <span>Swaps just one piece — your face, body, pose & background stay identical.</span>
                </div>
                {visibleItems.length === 0 ? <EmptyState text="No items here yet." /> : (
                  <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 xl:grid-cols-5">
                    {visibleItems.map((i) => (
                      <button
                        key={i.id}
                        type="button"
                        disabled={busy}
                        onClick={() => setPreviewItem(i)}
                        className={cn(
                          "group relative aspect-[3/4] overflow-hidden rounded-2xl border border-white/10 bg-muted text-left shadow-soft transition-all",
                          "hover:-translate-y-0.5 hover:border-primary/60 hover:shadow-elevated",
                          busy && "opacity-50 pointer-events-none",
                        )}
                      >
                        <ImageWithSkeleton src={i.image} alt={i.label} imageClassName="transition-transform duration-500 group-hover:scale-105" />
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent p-2.5">
                          <p className="truncate text-xs font-semibold text-white">{i.label}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </section>
        </div>

        {/* Overlays — unchanged behavior */}
        <GenerationOverlay
          open={busy || !!genError}
          stage={STAGES[stageIdx]}
          errorMessage={genError}
          onRetry={() => { setGenError(null); lastAttemptRef.current?.(); }}
          onCancel={() => setGenError(null)}
        />

        <BottomSheet open={!!previewVibe} onClose={() => setPreviewVibe(null)} title={previewVibe?.label}>
          {previewVibe && (
            <div className="space-y-4">
              <div className="relative overflow-hidden rounded-xl bg-muted">
                <img src={previewVibe.image} alt={previewVibe.label} className="aspect-[3/4] w-full object-cover" />
                <div className="absolute left-2 top-2 rounded-full bg-black/70 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-white">
                  Preview · your result will look like this
                </div>
              </div>
              {previewVibe.description && <p className="text-sm text-muted-foreground">{previewVibe.description}</p>}
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">What should change?</p>
                <div className="grid grid-cols-2 gap-2">
                  <ModeOption active={vibeMode === "scene"} onClick={() => setVibeMode("scene")} title="Scene only" sub="Same outfit, new place" />
                  <ModeOption active={vibeMode === "outfit"} onClick={() => setVibeMode("outfit")} title="Scene + outfit" sub="Restyle for the vibe" />
                </div>
              </div>
              <Button className="h-12 w-full gradient-primary text-primary-foreground" onClick={confirmVibe}>
                <Wand2 className="mr-2 h-4 w-4" /> Generate (1 credit)
              </Button>
              <p className="flex items-center justify-center gap-1.5 text-center text-xs text-muted-foreground">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                AI may generate inaccurate results. Use as inspiration, not a perfect preview.
              </p>
            </div>
          )}
        </BottomSheet>

        <BottomSheet open={!!previewLook} onClose={() => setPreviewLook(null)} title={previewLook?.label}>
          {previewLook && (
            <div className="space-y-4">
              <div className="relative overflow-hidden rounded-xl bg-muted">
                <img src={previewLook.image} alt={previewLook.label} className="aspect-[3/4] w-full object-cover" />
                <div className="absolute left-2 top-2 rounded-full bg-black/70 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-white">
                  Preview · your result will look like this
                </div>
              </div>
              <div>
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Style notes</p>
                <p className="text-sm text-muted-foreground">{previewLook.prompt}</p>
              </div>
              <Button className="h-12 w-full gradient-primary text-primary-foreground" onClick={confirmLook}>
                <Wand2 className="mr-2 h-4 w-4" /> Try this look (1 credit)
              </Button>
            </div>
          )}
        </BottomSheet>

        <BottomSheet open={!!previewItem} onClose={() => setPreviewItem(null)} title={previewItem?.label}>
          {previewItem && (
            <div className="space-y-4">
              <div className="overflow-hidden rounded-xl bg-muted">
                <img src={previewItem.image} alt={previewItem.label} className="aspect-square w-full object-contain p-4" />
              </div>
              <p className="text-sm text-muted-foreground">{previewItem.prompt}</p>
              <p className="text-xs text-muted-foreground">
                Just this piece changes — your face, body, pose & background stay exactly the same.
              </p>
              <Button className="h-12 w-full gradient-primary text-primary-foreground" onClick={confirmItem}>
                <Wand2 className="mr-2 h-4 w-4" /> Try this on me (1 credit)
              </Button>
            </div>
          )}
        </BottomSheet>

        <BottomSheet
          open={customItemSheetOpen}
          onClose={() => { setCustomItemSheetOpen(false); setCustomItemFile(null); setCustomItemPreview(null); }}
          title="Try your own item"
        >
          {customItemPreview && (
            <div className="space-y-4">
              <div className="overflow-hidden rounded-xl bg-muted">
                <img src={customItemPreview} alt="Your item" className="aspect-square w-full object-contain p-4" />
              </div>
              <div>
                <label htmlFor="custom-item-label" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Name (optional)</label>
                <input id="custom-item-label" name="customItemLabel" autoComplete="off" type="text" maxLength={40}
                  value={customItemLabel} onChange={(e) => setCustomItemLabel(e.target.value)}
                  className="mt-1 h-10 w-full rounded-md border border-input bg-background px-3 text-sm" placeholder="e.g. My new jacket" />
              </div>
              <p className="text-xs text-muted-foreground">
                We'll put just this item on you — face, pose & background stay the same. One-time use only (not saved).
              </p>
              <Button className="h-12 w-full gradient-primary text-primary-foreground" onClick={confirmCustomItem}>
                <Wand2 className="mr-2 h-4 w-4" /> Try it on (1 credit)
              </Button>
            </div>
          )}
        </BottomSheet>

        <CustomizeOutfitDialog busy={busy} hideTrigger
          open={customOpen} onOpenChange={setCustomOpen}
          onGenerate={(d) => generateOutfit(d, "Custom", "custom")} />
        <BuyCreditsDialog open={buyOpen} onOpenChange={setBuyOpen} defaultPackId={buyDefaultPack} />
        <BuyCreditsDialog open={outOfCreditsOpen} onOpenChange={setOutOfCreditsOpen} outOfCredits />
        <HighDemandDialog open={highDemandOpen} onOpenChange={setHighDemandOpen} />

        <PaywallModal
          open={!!paywall} variant={paywall}
          onClose={() => setPaywall(null)} onDismiss={handlePaywallDismiss}
          onBuy={handlePaywallBuy}
        />
      </div>
    </>
  );
};

const ThumbCard = ({ label, image, disabled, onClick, badge }: { label: string; image: string; disabled?: boolean; onClick: () => void; badge?: string }) => (
  <button type="button" disabled={disabled} onClick={onClick}
    className={cn(
      "group relative aspect-[3/4] overflow-hidden rounded-2xl border border-white/10 bg-muted text-left shadow-soft transition-all duration-300",
      "hover:-translate-y-1 hover:border-primary/60 hover:shadow-elevated",
      disabled && "opacity-50 pointer-events-none",
    )}
  >
    <ImageWithSkeleton src={image} alt={label} imageClassName="transition-transform duration-500 group-hover:scale-110" />
    <div aria-hidden className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
    {badge && (
      <div className="absolute left-2 top-2 rounded-full bg-primary/90 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary-foreground backdrop-blur-md shadow-soft">
        {badge}
      </div>
    )}
    <div className="absolute inset-x-0 bottom-0 p-4">
      <p className="text-base font-bold text-white drop-shadow-lg">{label}</p>
      <p className="mt-0.5 text-[11px] font-medium uppercase tracking-[0.18em] text-white/70 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
        Tap to preview →
      </p>
    </div>
  </button>
);

const FilterChip = ({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) => (
  <button type="button" onClick={onClick}
    className={cn(
      "shrink-0 whitespace-nowrap rounded-full border px-4 py-1.5 text-xs font-semibold transition-all",
      active
        ? "border-primary bg-primary text-primary-foreground shadow-soft"
        : "border-white/10 bg-card/40 text-muted-foreground hover:border-primary/40 hover:text-foreground",
    )}
  >{children}</button>
);

const ModeOption = ({ active, onClick, title, sub }: { active: boolean; onClick: () => void; title: string; sub: string }) => (
  <button type="button" onClick={onClick}
    className={cn("rounded-xl border p-3 text-left transition-all",
      active ? "border-primary bg-accent" : "border-border bg-background hover:border-primary/40")}
  >
    <p className="text-sm font-semibold">{title}</p>
    <p className="mt-0.5 text-xs font-semibold text-sidebar-primary">{sub}</p>
  </button>
);

const EmptyState = ({ text }: { text: string }) => (
  <div className="rounded-2xl border border-dashed border-white/15 bg-card/30 p-12 text-center backdrop-blur">
    <p className="text-sm text-muted-foreground">{text}</p>
  </div>
);
